import { memo } from 'react';
import { AlertBanner, Button } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { Mail, Send } from 'lucide-react';

import { INVITATION_VALID_DAYS } from '../../constants/memberDetail';
import { PERMISSIONS } from '../../constants/permissions';
import { usePermission } from '../../hooks/usePermission';
import { daysUntil, getRelativeDays } from '../../utils/memberDates';

const TONE = { invited: 'info', expired: 'warning', notInvited: 'warning' };
const ICON = { invited: Send, expired: Send, notInvited: Mail };

/**
 * Banner of a pending member (Invited, Expired, Not invited) with its invitation actions.
 * The actions need the invite permission; without it the banner is informative only.
 *
 * @param {object} props
 * @param {'invited'|'expired'|'notInvited'|null} props.banner From `getMemberStatusView`.
 * @param {object} props.member UI member
 * @param {() => void} props.onEditEmail
 * @param {() => void} props.onResend
 * @param {() => void} props.onSend
 */
function MemberStateBanner({ banner, member, onEditEmail, onResend, onSend }) {
  const { t } = useTranslation('people-access');
  const { allowed } = usePermission(PERMISSIONS.MEMBERS_INVITE);
  if (!banner) return null;

  const action = allowed ? (
    <>
      <Button variant="tertiary" size="sm" onClick={onEditEmail}>
        {t('memberDetail.banner.editEmail')}
      </Button>
      <Button size="sm" onClick={banner === 'notInvited' ? onSend : onResend}>
        {t(`memberDetail.banner.${banner}.action`)}
      </Button>
    </>
  ) : undefined;

  const title = t(`memberDetail.banner.${banner}.title`, { count: daysUntil(member.invitationExpiresAt) });
  // "stopped working 5 days ago": only days or weeks read well in the sentence, else "recently".
  const since = getRelativeDays(member.invitationExpiresAt);
  const when = since.count ? t(since.key, { count: since.count }) : t('memberDetail.banner.expired.recently');
  const message = t(`memberDetail.banner.${banner}.message`, {
    email: member.email,
    name: member.displayName,
    days: INVITATION_VALID_DAYS,
    when,
  });

  return (
    <AlertBanner tone={TONE[banner]} icon={ICON[banner]} title={title} action={action}>
      {message}
    </AlertBanner>
  );
}

export default memo(MemberStateBanner);
